import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Tabs } from "antd";
import {
  MyAccountModule,
  MyAccountSubModule,
} from "@companion-core/shared/app/Interfaces/myAccount";
import { DynamicSection } from "@companion-core/shared/app/Components/MyAccount/DynamicSection";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { Banner } from "@companion-core/shared/app/Components/Banner";
import { useAccountNavigationStore } from "@companion-core/shared/app/Store/accountNavigationStore";
import { useMemberStore } from "@companion-core/shared/app/Store/memberStore";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { useTabsAutoActivate } from "@companion-core/web/src/Hooks/useTabsAutoActivate";
import "@companion-core/web/src/Assets/Styles/scss/myAccount.scss";

const TabContent = ({
  testID,
  icon,
  label,
  isActive,
}: {
  testID: string;
  icon: string;
  label: string;
  isActive: boolean;
}) => {
  return (
    <div
      data-testid={testID}
      className={`my-account-desktop__tab-content ${
        isActive ? "my-account-desktop__tab-content--active body-text-5" : "body-text-6"
      }`}>
      <FontIcon
        aria-hidden={true}
        className={`${
          isActive
            ? "my-account-desktop__tab-content-icon--active"
            : "my-account-desktop__tab-content-icon"
        }`}
        name={icon}
        size={24}
      />
      <span className="my-account-desktop__tab-label">{label}</span>
    </div>
  );
};

const MyAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { section } = useParams();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { hideBackButtonOnHome, openPopover } = useAccountNavigationStore();
  const { resetValidationBanner } = useMemberStore();
  const myAccountModule: MyAccountModule = getModule("my-account");
  const headerImage = useMemo(() => myAccountModule?.assets.headerImg, [myAccountModule]);
  const enabledMyAccountEntries = useMemo(
    () =>
      Object.entries(myAccountModule?.modules)
        ?.filter((module) => !!(module[1] as MyAccountSubModule)?.enabled)
        ?.sort((moduleA, moduleB) => moduleA?.[1]?.order - moduleB?.[1]?.order) ?? [],
    [myAccountModule]
  );
  const enabledMyAccountModules = useMemo(
    () => Object.fromEntries(enabledMyAccountEntries),
    [enabledMyAccountEntries]
  );
  const selectedModuleByDefault = useMemo(
    () => (Object.values(enabledMyAccountEntries[0])[0] as string) ?? "information",
    [enabledMyAccountEntries]
  );
  const [activeTab, setActiveTab] = useState<string>(section ?? selectedModuleByDefault);

  useEffect(() => {
    setActiveTab(section ?? selectedModuleByDefault);
    resetValidationBanner();
  }, [section]);

  useEffect(() => {
    if (isMobileSize) {
      hideBackButtonOnHome();
      openPopover();
    }
  }, [isMobileSize]);

  const handleTabChange = (key: string) => {
    navigate(`/my-account/${key}`);
  };

  const tabsContainerRef = useTabsAutoActivate<HTMLDivElement>(handleTabChange, activeTab);

  const tabItems: { key: string; label: ReactElement; children: ReactElement }[] = useMemo(
    () =>
      Object.entries(enabledMyAccountModules).map(([myAccountModuleKey, myAccountModuleValue]) => {
        return {
          key: myAccountModuleKey,
          label: (
            <TabContent
              testID={`tab__${myAccountModuleKey}`}
              icon={(myAccountModuleValue as Record<string, unknown>).icon as string}
              label={t(`my-account.menu.items.${myAccountModuleKey}`)}
              isActive={activeTab === myAccountModuleKey}
            />
          ),
          children: <DynamicSection defaultSection={selectedModuleByDefault} />,
        };
      }),
    [enabledMyAccountModules, activeTab]
  );

  return (
    !isMobileSize && (
      <div className="my-account-desktop" ref={tabsContainerRef}>
        <Banner
          imageUrl={headerImage}
          title={t("my-account.banner.title")}
          overlap={false}
          boxShadow
        />
        {enabledMyAccountEntries.length > 1 ? (
          <Tabs
            id="my-account-tabs"
            className="my-account-desktop__tabs"
            defaultActiveKey={section ?? selectedModuleByDefault}
            activeKey={activeTab}
            onChange={(key) => handleTabChange(key)}
            centered
            items={tabItems}
          />
        ) : (
          <div className="my-account-desktop__single-section">
            <DynamicSection defaultSection={selectedModuleByDefault} />
          </div>
        )}
      </div>
    )
  );
};

export default MyAccount;
